/* eslint-disable react-hooks/exhaustive-deps */
import React, { Suspense, useEffect, useState } from "react";
import {
  BrowserRouter as Router,
  Route,
  Routes,
  useNavigate,
  useLocation,
} from "react-router-dom";
import {
  LoadingProvider,
  useLoading,
} from "./Components/Common/LoadingSpinner/LoadingContext";
import { ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import LoadingSpinner from "./Components/Common/LoadingSpinner/LoadingSpinner";
import { fetchData } from "./generalUtils";
import { movedEventRedirectUrl } from "./movedEventRedirect";
import {
  deleteAllEventDatabases,
  deleteExpiredEventDatabases,
} from "./IndexedDBUtils";
import { GifProvider } from "./GifContext";
import DesignStudioFrame from "./Pages/DesignStudio/DesignStudioFrame";

// Swingies Components
import SwingieHomePage from "./Pages/AttractionApps/Swingies/Home/SwingieHome";
import SwingieFaceGalleryPage from "./Pages/AttractionApps/Swingies/FaceGallery/SwingieFaceGallery";
import SwingieBodyGalleryPage from "./Pages/AttractionApps/Swingies/BodyGallery/SwingieBodyGallery";
import SwingieEditYourCharacterPage from "./Pages/AttractionApps/Swingies/EditYourCharacter/SwingieEditYourCharacter";
import SwingieResultPage from "./Pages/AttractionApps/Swingies/Result/SwingieResult";

// MiniMove Components for development
import MinimoveHomePage from "./Pages/AttractionApps/MiniMove/Home/MinimoveHome";
import MinimoveFaceGalleryPage from "./Pages/AttractionApps/MiniMove/FaceGallery/MinimoveFaceGallery";
import MinimoveBodyGalleryPage from "./Pages/AttractionApps/MiniMove/BodyGallery/MinimoveBodyGallery";
import MinimoveResultPage from "./Pages/AttractionApps/MiniMove/Result/MinimoveResult";

// MiniMe Components
import MiniMeHomePage from "./Pages/AttractionApps/MiniMe/Home/MiniMeHome";

// independent pages
import PreOrderUploadPage from "./Pages/PreOrder/PreOrderUpload";
import ImageProtection from "./Components/Common/ImageProtection/ImageProtection";
import WhatsAppButton from "./Components/Common/WhatsAppButton/WhatsAppButton";
import EventNotFound from "./Pages/NonAppPages/EventNotFound/EventNotFound";
import EventStatusPage from "./Pages/NonAppPages/EventStatusPage/EventStatusPage";
import EventGallery from "./Pages/NonAppPages/EventGallery/EventGallery";
import ErrorBoundary from "./Components/Common/ErrorBoundary/ErrorBoundary";
import { SERVER_URL } from "./config/serverUrl";
import { useAppContext } from "./AppContext";
import EventRouteGate from "./EventRouteGate";
import { isFaceFlowEventName } from "./Pages/AttractionApps/shared/faceClaimEvent";
import { useFlowInactivity } from "./Pages/AttractionApps/shared/useFlowInactivity";
import { useClaimRenewal } from "./Pages/AttractionApps/shared/useClaimRenewal";
import { useAdminReleaseWatch } from "./Pages/AttractionApps/shared/adminRelease";

// Code-split heavy surfaces out of the main (guest-facing) bundle:
// - AdminPanel: the operator console — guests never load it.
// - MiniMe CameraCapture: the heavy camera/canvas stack — only the MiniMe
//   capture page needs it.
const AdminPanel = React.lazy(() => import("./Pages/AdminPanel/AdminEntry"));
const MiniMeCameraCapture = React.lazy(() =>
  import("./Pages/AttractionApps/MiniMe/Capture/CameraCapture")
);
// The cloud-web landing page the desktop app opens in the system browser for
// its Google sign-in handoff (Google restricts authorized JS origins, so the
// loopback desktop app cannot run Google Sign-In itself). Plain cloud route,
// not part of the desktop bundle.
const CloudDesktopConnect = React.lazy(() => import("./desktop/CloudDesktopConnect"));
// Operator console sign-in: guests never open it, so its dark card stylesheet stays out of the main bundle.
const Login = React.lazy(() => import("./Login"));

const serverUrl = SERVER_URL;

// Suspense fallback for the console chunk: a minimal dark screen so the
// operator gets no white flash while the chunk downloads. Inline-styled —
// the darkroom stylesheet itself lives inside the lazy chunk.
const adminChunkFallback = (
  <div
    style={{
      width: "100%",
      alignSelf: "stretch",
      minHeight: "100vh",
      background: "#101318",
      color: "#a7b2c2",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      fontFamily: '"Heebo", "Segoe UI", system-ui, sans-serif',
      direction: "rtl",
    }}
  >
    טוען את קונסולת הניהול…
  </div>
);

// This function returns the appropriate components for an event based on its name
const getAttractionComponents = (eventType) => {
  // Event type from the event name (e.g., "swingies" or "minimove")

  // Return the appropriate components based on the event type
  switch (eventType) {
    case "swingies":
      return {
        Home: SwingieHomePage,
        FaceGallery: SwingieFaceGalleryPage,
        BodyGallery: SwingieBodyGalleryPage,
        EditYourCharacter: SwingieEditYourCharacterPage,
        Result: SwingieResultPage,
      };
    case "minimove":
      return {
        Home: MinimoveHomePage,
        FaceGallery: MinimoveFaceGalleryPage,
        BodyGallery: MinimoveBodyGalleryPage,
        Result: MinimoveResultPage,
      };
    case "minime":
      return {
        Home: MiniMeHomePage,
        CameraCapture: MiniMeCameraCapture,
      };
    default:
      return null;
  }
};

const App = () => {
  return (
    <Router>
      <GifProvider>
        <LoadingProvider>
          <ImageProtection>
            <EnsureHomeOnReload>
              <ToastContainer />
              <Routes>
                {/* Gallery page - independent of event running status */}
                <Route
                  path="/:eventName/gallery"
                  element={<EventGallery />}
                />
                <Route
                  exact
                  path="/:eventName/:page?"
                  element={
                    <ErrorBoundary variant="guest">
                      <EventRouteGate>
                        <EventWrapper />
                      </EventRouteGate>
                    </ErrorBoundary>
                  }
                />
                <Route
                  path="/login"
                  element={
                    <Suspense fallback={null}>
                      <Login />
                    </Suspense>
                  }
                />
                <Route
                  path="/desktop-connect"
                  element={
                    <Suspense fallback={null}>
                      <CloudDesktopConnect />
                    </Suspense>
                  }
                />
                {/* Design studio (static segment outranks /:eventName in v6 ranking) */}
                <Route path="/redesign" element={<DesignStudioFrame />} />
                <Route
                  path="/"
                  element={
                    <ErrorBoundary variant="admin">
                      <Suspense fallback={adminChunkFallback}>
                        <AdminPanel />
                      </Suspense>
                    </ErrorBoundary>
                  }
                />
                <Route path="*" element={<EventNotFound />} />
              </Routes>
            </EnsureHomeOnReload>
            <LoadingHandler />
          </ImageProtection>
        </LoadingProvider>
      </GifProvider>
    </Router>
  );
};

const EventWrapper = () => {
  const [eventData, setEventData] = useState(null); // Holds event data fetched from the server
  const [components, setComponents] = useState(null); // Holds the components for the current event
  const location = useLocation();
  const { eventName } = useAppContext(); // set by EventRouteGate == route name

  const [eventType, setEventType] = useState(null);
  // Determine which component to render based on the current page
  const page = location.pathname.split("/")[2];
  // Admin live preview (?admin_preview=1): the console embeds guest pages
  // to mirror them — it must see the page itself even when the event is
  // not currently running, and without the floating WhatsApp button.
  // Trust model: the query flag alone is guessable from a shared link, so
  // it is honored only with evidence this browser is the operator's —
  // either the page is framed (the console's same-origin iframe) or the
  // console itself has run in this browser and left its localStorage
  // marker (set by AdminPanel on mount; guests never have it).
  const adminPreview =
    new URLSearchParams(location.search).get("admin_preview") === "1" &&
    (window.self !== window.top ||
      localStorage.getItem("minime_admin_console") === "1");

  // Transient check-event failures get a lightweight retry state instead
  // of EventNotFound (which would wipe every event's IndexedDB cache).
  const [checkError, setCheckError] = useState(false);
  const [checkAttempt, setCheckAttempt] = useState(0);
  // check-event answered 400/404: a definitive "no such event". Non-destructive
  // (no retry, no IndexedDB wipe) — unlike exists:false on a 2xx answer.
  const [notFound, setNotFound] = useState(false);

  // ONE 90 s wall-clock inactivity deadline across Home + every Swingies/MiniMove guest page (the gallery and the
  // later pages only display it). Expiry releases the editing hold and navigates to the validated event Home.
  // Admin preview, MiniMe, upload/status pages and a not-running event never run it.
  const flowPage = !["face-gallery", "body-gallery", "edit-your-character", "result"].includes(page);
  const faceFlowEnabled =
    Boolean(eventData && eventData.exists) &&
    (eventData.progressStatus === "running" || Boolean(eventData.lite_mode)) &&
    isFaceFlowEventName(eventName) &&
    !adminPreview &&
    !["upload", "status", "capture", "gallery"].includes(page);
  useFlowInactivity({ enabled: faceFlowEnabled, isHome: flowPage });
  // An operator release of this guest's hold (observed `revoked` only) returns the guest Home.
  useAdminReleaseWatch({ enabled: faceFlowEnabled, page });
  // Past the face gallery real activity keeps renewing the editing hold (105 s server lease).
  useClaimRenewal({ enabled: faceFlowEnabled, page });

  useEffect(() => {
    // The gate guarantees the validated route name equals the context name.
    const encodedEventName = encodeURIComponent(eventName);

    const extractedEventType = eventName.split("_")[0].toLowerCase();
    setEventType(extractedEventType);

    // Check if the event exists by making an API call
    let cancelled = false;
    let retryTimerId = null;
    const checkEventExists = async () => {
      let data;
      try {
        data = await fetchData(serverUrl, "/api/check-event", encodedEventName);
        if (cancelled) return;
        if (data.exists) {
          // Set the appropriate components for the current event
          setComponents(getAttractionComponents(extractedEventType));
        }
        setCheckError(false);
        setEventData(data);
      } catch (error) {
        if (cancelled) return;
        if (error && error.status === 404) {
          // Moved to another environment: continue on the same path there.
          const movedUrl = movedEventRedirectUrl(error.data, window.location);
          if (movedUrl) {
            window.location.replace(movedUrl);
            return;
          }
        }
        if (error && (error.status === 400 || error.status === 404)) {
          console.error("Event not found:", error);
          setCheckError(false);
          setNotFound(true);
          return;
        }
        // A network blip must NOT be treated as exists:false — that path
        // renders EventNotFound and deletes all event caches. Show a
        // retry message and re-run the check shortly.
        console.error("Error checking event:", error);
        setCheckError(true);
        retryTimerId = setTimeout(
          () => setCheckAttempt((attempt) => attempt + 1),
          3000
        );
      }
    };

    checkEventExists();
    return () => {
      cancelled = true;
      if (retryTimerId) clearTimeout(retryTimerId);
    };
  }, [checkAttempt, eventName]);

  if (notFound) {
    return <EventNotFound />;
  }

  // Show loading spinner while event data is being fetched
  if (!eventData) {
    if (checkError) {
      return (
        <div className="rtl" style={{ textAlign: "center", padding: "40vh 20px" }}>
          שגיאת רשת — טוען שוב...
        </div>
      );
    }
    return <LoadingSpinner />;
  }

  // If the event does not exist, show the EventNotFound page.
  // In admin preview the page runs inside the operator's browser — never
  // wipe the console's own per-event caches from there.
  if (!eventData.exists) {
    if (!adminPreview) {
      deleteAllEventDatabases(); // Delete cached images if event not found
    }
    return <EventNotFound />;
  }

  if (page === "upload") {
    if (!eventData.upload_enabled) {
      return <EventNotFound />;
    }
    return <PreOrderUploadPage /*eventName={eventName}*/ />;
  }

  // If the event is not currently running and lite_mode is not enabled, show the EventStatusPage
  // lite_mode allows access to the event without time-based restrictions
  // (admin_preview bypasses the gate so the console always mirrors the page)
  if (
    eventData.progressStatus !== "running" &&
    !eventData.lite_mode &&
    !adminPreview
  ) {
    deleteAllEventDatabases(); // Delete cached images if event is not running
    return (
      <EventStatusPage
        status={eventData.progressStatus}
        startTime={eventData.startTime}
      />
    );
  }

  // Show loading spinner while components are being set
  if (!components) {
    return <LoadingSpinner />;
  }

  // Destructure the components object to get the specific components for the event
  const {
    Home,
    FaceGallery,
    BodyGallery,
    EditYourCharacter = null,
    Result,
    CameraCapture = null,
  } = components;

  const homeProps = {
    reFace: eventData.reFace,
  };

  if (eventType === "swingies") {
    homeProps.swingiesSelectionLimit =
      eventData.swingies_home_selection_limit;
  }

  switch (page) {
    case "face-gallery":
      return (
        <>
          <FaceGallery />
          {!adminPreview && <WhatsAppButton eventType={eventType} />}
        </>
      );
    case "body-gallery":
      return (
        <>
          <BodyGallery />
          <WhatsAppButton eventType={eventType} />
        </>
      );
    case "edit-your-character":
      return EditYourCharacter ? (
        <>
          <EditYourCharacter />
          <WhatsAppButton eventType={eventType} />
        </>
      ) : (
        <>
          <Home {...homeProps} />
          <WhatsAppButton eventType={eventType} />
        </>
      );
    case "result":
      return (
        <>
          <Result siteRedirect={eventData.site_redirect} />
          <WhatsAppButton eventType={eventType} />
        </>
      );
    case "status":
      return (
        <EventStatusPage
          status={eventData.progressStatus}
          startTime={eventData.startTime}
        />
      ); // No WhatsApp button here
    case "capture":
      // CameraCapture is a React.lazy component (chunk-split) — needs a
      // Suspense boundary at its render site.
      return CameraCapture ? (
        <Suspense fallback={<LoadingSpinner />}>
          <CameraCapture />
        </Suspense>
      ) : (
        <Home {...homeProps} />
      );
    default:
      return (
        <>
          <Home {...homeProps} />
          <WhatsAppButton eventType={eventType} />
        </>
      );
  }
};

const EnsureHomeOnReload = ({ children }) => {
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    // console.log("[EnsureHomeOnReload] useEffect starting, current path:", location.pathname);

    // Clean up databases for events that no longer exist
    const cleanupExpiredDatabases = async () => {
      // console.log("[EnsureHomeOnReload] Calling deleteExpiredEventDatabases...");
      try {
        await deleteExpiredEventDatabases(serverUrl);
        // console.log("[EnsureHomeOnReload] deleteExpiredEventDatabases completed successfully");
      } catch (error) {
        console.error("[EnsureHomeOnReload] Error cleaning up expired event databases:", error);
      }
    };

    cleanupExpiredDatabases();

    // Ensure that on reload, the user is navigated to the correct event page
    const navigationEntries = performance.getEntriesByType("navigation");
    if (
      navigationEntries.length > 0 &&
      navigationEntries[0].type === "reload"
    ) {
      if (location.pathname !== "/") {
        const pathParts = location.pathname.split("/");
        const eventName = pathParts[1];

        // The design studio is not an event page — never bounce it
        if (eventName === "redesign") {
          return;
        }

        // Don't redirect for special pages that should persist on reload
        if (pathParts.length > 2 && (pathParts[2] === "upload" || pathParts[2] === "gallery")) {
          return;
        }
        // The console's live-preview iframe reloads too — keep the gallery
        // on-screen instead of bouncing to Home and dropping the flag.
        if (
          pathParts[2] === "face-gallery" &&
          new URLSearchParams(location.search).get("admin_preview") === "1"
        ) {
          return;
        }
        navigate(`/${eventName}`);
      }
    }
  }, []);

  return children;
};

const LoadingHandler = () => {
  const { isLoading } = useLoading();
  // Show loading spinner if any component is currently loading
  return isLoading ? <LoadingSpinner /> : null;
};

export default App;
